import axios from 'axios'

// Template type to API endpoint mapping
const API_ENDPOINTS = {
  engagement_letter: '/engagement_letter_templates',
  docket_entry_notification: '/docket_entry_type_notifications',
  short_message: '/short_message_templates',
}

export default {
  // Generic action to fetch templates by type
  async fetchTemplatesByType ({ commit }, { templateType, params = {} }) {
    if (!API_ENDPOINTS[templateType]) {
      throw new Error(`Unknown template type: ${templateType}`)
    }

    commit('setLoadingByType', { templateType, loading: true })

    try {
      const response = await axios.get(API_ENDPOINTS[templateType], { params })
      const templates = response.data.data || response.data

      commit('setTemplatesByType', { templateType, templates })
      return templates
    } finally {
      commit('setLoadingByType', { templateType, loading: false })
    }
  },

  // Generic action to create a template
  async createTemplate ({ commit }, { templateType, templateData }) {
    if (!API_ENDPOINTS[templateType]) {
      throw new Error(`Unknown template type: ${templateType}`)
    }

    const response = await axios.post(API_ENDPOINTS[templateType], templateData)
    const template = response.data.data || response.data

    commit('addTemplate', { templateType, template })
    return template
  },

  // Generic action to update a template
  async updateTemplate ({ commit }, { templateType, templateId, templateData }) {
    if (!API_ENDPOINTS[templateType]) {
      throw new Error(`Unknown template type: ${templateType}`)
    }

    const response = await axios.put(`${API_ENDPOINTS[templateType]}/${templateId}`, templateData)
    const template = response.data.data || response.data

    commit('updateTemplate', { templateType, template })
    return template
  },

  // Generic action to delete a template
  async deleteTemplate ({ commit }, { templateType, templateId }) {
    if (!API_ENDPOINTS[templateType]) {
      throw new Error(`Unknown template type: ${templateType}`)
    }

    await axios.delete(`${API_ENDPOINTS[templateType]}/${templateId}`)
    commit('removeTemplate', { templateType, templateId })
    return true
  },

  // Fetch languages for template translations
  async fetchLanguages ({ commit }) {
    commit('setLanguagesLoading', true)

    try {
      const response = await axios.get('/languages')
      const languages = response.data.data || response.data

      commit('setLanguages', languages)
      return languages
    } finally {
      commit('setLanguagesLoading', false)
    }
  },

  // Specific actions for engagement letters
  async fetchEngagementLetters ({ dispatch }, params = {}) {
    return dispatch('fetchTemplatesByType', {
      templateType: 'engagement_letter',
      params,
    })
  },

  async createEngagementLetter ({ dispatch }, templateData) {
    return dispatch('createTemplate', {
      templateType: 'engagement_letter',
      templateData,
    })
  },

  async updateEngagementLetter ({ dispatch }, { templateId, templateData }) {
    return dispatch('updateTemplate', {
      templateType: 'engagement_letter',
      templateId,
      templateData,
    })
  },

  async deleteEngagementLetter ({ dispatch }, templateId) {
    return dispatch('deleteTemplate', {
      templateType: 'engagement_letter',
      templateId,
    })
  },

  // Specific actions for docket entry notifications
  async fetchDocketEntryNotifications ({ dispatch }, params = {}) {
    return dispatch('fetchTemplatesByType', {
      templateType: 'docket_entry_notification',
      params,
    })
  },

  async createDocketEntryNotification ({ dispatch }, templateData) {
    return dispatch('createTemplate', {
      templateType: 'docket_entry_notification',
      templateData,
    })
  },

  async updateDocketEntryNotification ({ dispatch }, { templateId, templateData }) {
    return dispatch('updateTemplate', {
      templateType: 'docket_entry_notification',
      templateId,
      templateData,
    })
  },

  async deleteDocketEntryNotification ({ dispatch }, templateId) {
    return dispatch('deleteTemplate', {
      templateType: 'docket_entry_notification',
      templateId,
    })
  },

  // Specific actions for short message templates
  async fetchShortMessageTemplates ({ dispatch }, params = {}) {
    return dispatch('fetchTemplatesByType', {
      templateType: 'short_message',
      params,
    })
  },

  async createShortMessageTemplate ({ dispatch }, templateData) {
    return dispatch('createTemplate', {
      templateType: 'short_message',
      templateData,
    })
  },

  async updateShortMessageTemplate ({ dispatch }, { templateId, templateData }) {
    return dispatch('updateTemplate', {
      templateType: 'short_message',
      templateId,
      templateData,
    })
  },

  async deleteShortMessageTemplate ({ dispatch }, templateId) {
    return dispatch('deleteTemplate', {
      templateType: 'short_message',
      templateId,
    })
  },
}
